/**
 * Messages.
 *
 *  - Clients: a chat with their coach. Header shows who they're talking to
 *    ("Your coach"); tabs if they've talked with more than one coach. Sending
 *    with no thread yet goes to their assigned coach server-side.
 *  - Coaches: an inbox. Their clients (GET /api/coach/clients — assigned, plus
 *    unassigned for supertrainers) merged with anyone they already have a
 *    conversation with, newest activity first, unread badges. Desktop shows
 *    the list beside the thread; phone shows the list, then the thread with a
 *    back arrow. A coach can start a thread with any listed client.
 *
 * Threads read as conversations: sender name + initials on each run of
 * messages, "You" on yours, composer pinned to the bottom.
 */

import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { isTrainerRole, type Conversation, type Message } from '@fitplatform/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useIsDesktop } from '@/lib/useIsDesktop';

interface CoachClient {
  id: string;
  display_name: string;
  email: string;
  avatar_url: string | null;
  unassigned?: boolean;
}

/** One row in a coach's inbox: a client and their thread (if any). */
interface InboxRow {
  id: string;
  name: string;
  avatar: string | null;
  role: string;
  lastMessage: string | null;
  lastAt: string | null;
  unread: number;
  unassigned: boolean;
}

function initialsOf(name: string): string {
  if (name.includes('@')) return name[0].toUpperCase();
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?';
}

function Avatar({ name, url, size = 40 }: { name: string; url?: string | null; size?: number }) {
  return (
    <div
      className="rounded-full flex items-center justify-center font-bold flex-shrink-0 overflow-hidden"
      style={{ width: size, height: size, fontSize: size * 0.38, background: 'var(--gold)', color: 'var(--navy)' }}
      aria-hidden
    >
      {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : initialsOf(name)}
    </div>
  );
}

function parseTs(iso: string): Date {
  return new Date(iso.includes('T') || iso.includes('Z') ? iso : iso.replace(' ', 'T') + 'Z');
}

/** Local date + time (device timezone — never hardcoded UTC). */
function fmtTime(iso: string): string {
  const d = parseTs(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return sameDay ? time : `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} · ${time}`;
}

export default function MessagesPage() {
  const { user } = useAuth();
  const isDesktop = useIsDesktop();
  const iAmCoach = isTrainerRole(user?.role);
  const [selected, setSelected] = useState<string | null>(null);

  const convos = useQuery({
    queryKey: ['conversations'],
    queryFn: () =>
      api.get<{ conversations: Conversation[]; coach?: { id: string; name: string; avatar_url: string | null } | null }>(
        '/api/messages/conversations'
      ),
    refetchInterval: 30_000,
  });
  const conversations = convos.data?.conversations ?? [];

  const clients = useQuery({
    queryKey: ['coach-clients'],
    queryFn: () => api.get<{ clients: CoachClient[] }>('/api/coach/clients'),
    enabled: iAmCoach,
  });

  // ---- Coach inbox rows: clients ∪ existing conversations ----
  const rows: InboxRow[] = [];
  if (iAmCoach) {
    const byId = new Map<string, InboxRow>();
    for (const c of clients.data?.clients ?? []) {
      byId.set(c.id, {
        id: c.id, name: c.display_name, avatar: c.avatar_url, role: 'user',
        lastMessage: null, lastAt: null, unread: 0, unassigned: !!c.unassigned,
      });
    }
    for (const cv of conversations) {
      const row = byId.get(cv.partner_id) ?? {
        id: cv.partner_id, name: cv.partner_name, avatar: cv.partner_avatar ?? null,
        role: cv.partner_role ?? 'user', lastMessage: null, lastAt: null, unread: 0, unassigned: false,
      };
      row.lastMessage = cv.last_message;
      row.lastAt = cv.last_message_at;
      row.unread = cv.unread_count;
      byId.set(cv.partner_id, row);
    }
    rows.push(
      ...[...byId.values()].sort((a, b) => {
        if (a.lastAt && b.lastAt) return parseTs(b.lastAt).getTime() - parseTs(a.lastAt).getTime();
        if (a.lastAt) return -1;
        if (b.lastAt) return 1;
        return a.name.localeCompare(b.name);
      })
    );
  }

  // ---- Who the open thread is with ----
  let partner: { id: string; name: string; avatar: string | null; label: string } | null = null;
  if (iAmCoach) {
    const pick = rows.find((r) => r.id === selected) ?? (isDesktop ? rows[0] : undefined);
    if (pick) {
      partner = {
        id: pick.id, name: pick.name, avatar: pick.avatar,
        label: isTrainerRole(pick.role) ? 'Coach' : pick.unassigned ? 'Client · no coach assigned' : 'Client',
      };
    }
  } else {
    const cv =
      conversations.find((c) => c.partner_id === selected) ??
      conversations.find((c) => c.partner_id === user?.trainer_id) ??
      conversations[0];
    if (cv) {
      partner = {
        id: cv.partner_id, name: cv.partner_name, avatar: cv.partner_avatar ?? null,
        label: cv.partner_id === user?.trainer_id ? 'Your coach' : 'Coach',
      };
    }
  }

  // Client with no messages yet: still show their coach's name and photo.
  const coach = convos.data?.coach;
  const fallbackPartner = coach
    ? { id: coach.id, name: coach.name, avatar: coach.avatar_url, label: 'Your coach' }
    : { id: '', name: 'Your coach', avatar: null, label: 'Your messages go to your coach' };

  const height = isDesktop ? 'calc(100dvh - 72px)' : 'calc(100dvh - 80px)';
  const title = (
    <div className="px-4 pt-[max(16px,env(safe-area-inset-top))] pb-3">
      <h1 className="display" style={{ fontSize: '1.9rem' }}>Messages</h1>
    </div>
  );

  // ---------------- Coach ----------------
  if (iAmCoach) {
    const loading = clients.isLoading || convos.isLoading;
    const list = (
      <InboxList
        rows={rows}
        loading={loading}
        activeId={partner?.id ?? null}
        onPick={(id) => setSelected(id)}
      />
    );

    if (isDesktop) {
      return (
        <div className="flex flex-col" style={{ height }}>
          {title}
          <div className="flex-1 min-h-0 flex gap-4 px-4">
            <div className="w-72 flex-shrink-0 overflow-y-auto rounded-lg" style={{ background: 'var(--surface)', border: '1px solid rgba(244,239,227,0.08)' }}>
              {list}
            </div>
            <div className="flex-1 min-w-0 flex flex-col">
              {partner ? (
                <Thread partner={partner} meId={user?.id} me={{ name: 'You', avatar: user?.avatar_url ?? null }} />
              ) : (
                <p className="text-sm text-center mt-16" style={{ color: 'var(--cream-muted)' }}>
                  {loading ? 'Loading…' : 'Pick a client to start a conversation.'}
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Phone: list, or the open thread with a back arrow.
    return (
      <div className="flex flex-col" style={{ height }}>
        {partner ? (
          <>
            <div className="px-4 pt-[max(16px,env(safe-area-inset-top))] pb-2">
              <button onClick={() => setSelected(null)} className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--gold-muted)' }}>
                <ArrowLeft size={16} /> All conversations
              </button>
            </div>
            <Thread partner={partner} meId={user?.id} me={{ name: 'You', avatar: user?.avatar_url ?? null }} />
          </>
        ) : (
          <>
            {title}
            <div className="flex-1 overflow-y-auto px-4">{list}</div>
          </>
        )}
      </div>
    );
  }

  // ---------------- Client ----------------
  return (
    <div className="flex flex-col" style={{ height }}>
      {title}
      {conversations.length > 1 && (
        <div className="flex gap-2 px-4 pb-3 overflow-x-auto no-scrollbar">
          {conversations.map((c) => {
            const active = c.partner_id === partner?.id;
            return (
              <button
                key={c.partner_id}
                onClick={() => setSelected(c.partner_id)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs whitespace-nowrap"
                style={{
                  background: active ? 'rgba(201,168,106,0.15)' : 'var(--surface)',
                  color: active ? 'var(--gold)' : 'var(--gray-400)',
                  border: active ? '1px solid var(--gold-muted)' : '1px solid transparent',
                }}
              >
                {c.partner_name}
                {c.unread_count > 0 && (
                  <span className="rounded-full px-1.5 text-[10px] font-bold" style={{ background: 'var(--gold)', color: 'var(--navy)' }}>
                    {c.unread_count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
      <Thread
        partner={partner ?? fallbackPartner}
        meId={user?.id}
        me={{ name: 'You', avatar: user?.avatar_url ?? null }}
        clientWithoutThread={!partner}
      />
    </div>
  );
}

/** Coach inbox list. */
function InboxList({ rows, loading, activeId, onPick }: {
  rows: InboxRow[]; loading: boolean; activeId: string | null; onPick: (id: string) => void;
}) {
  if (loading && rows.length === 0) {
    return <p className="text-sm p-4" style={{ color: 'var(--cream-muted)' }}>Loading…</p>;
  }
  if (rows.length === 0) {
    return (
      <p className="text-sm p-4" style={{ color: 'var(--cream-muted)' }}>
        No clients assigned to you yet. Assign clients in the admin app under Clients.
      </p>
    );
  }
  return (
    <div className="flex flex-col py-1">
      {rows.map((r) => {
        const active = r.id === activeId;
        return (
          <button
            key={r.id}
            onClick={() => onPick(r.id)}
            className="flex items-center gap-3 px-3 py-3 text-left transition-colors"
            style={{ background: active ? 'rgba(201,168,106,0.10)' : 'transparent' }}
          >
            <Avatar name={r.name} url={r.avatar} size={36} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium truncate" style={{ color: active ? 'var(--gold)' : 'var(--cream)' }}>{r.name}</span>
                {r.unassigned && <span className="text-[9px] uppercase tracking-wider" style={{ color: 'var(--gray-500)' }}>Unassigned</span>}
              </div>
              <p className="text-xs truncate" style={{ color: 'var(--gray-500)' }}>
                {r.lastMessage ?? 'No messages yet'}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              {r.lastAt && <span className="text-[10px]" style={{ color: 'var(--gray-500)' }}>{fmtTime(r.lastAt)}</span>}
              {r.unread > 0 && (
                <span className="rounded-full px-1.5 text-[10px] font-bold" style={{ background: 'var(--gold)', color: 'var(--navy)' }}>
                  {r.unread}
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/** A conversation with one person: header, messages, composer. */
function Thread({ partner, meId, me, clientWithoutThread = false }: {
  partner: { id: string; name: string; avatar: string | null; label: string };
  meId: string | undefined;
  /** The signed-in user's photo, shown beside their own messages. */
  me?: { name: string; avatar: string | null };
  /** Client who hasn't messaged yet: send goes to their coach server-side. */
  clientWithoutThread?: boolean;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const partnerId = clientWithoutThread ? null : partner.id;

  const thread = useQuery({
    queryKey: ['thread', partnerId],
    queryFn: () => api.get<{ messages: Message[] }>(`/api/messages/${partnerId}`),
    enabled: !!partnerId,
    refetchInterval: 15_000,
  });
  const messages = thread.data?.messages ?? [];

  // Opening a thread marks it read server-side; refresh unread badges.
  useEffect(() => {
    if (thread.isSuccess) qc.invalidateQueries({ queryKey: ['conversations'] });
  }, [thread.isSuccess, partnerId, qc]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length, partnerId]);

  const send = useMutation({
    mutationFn: (content: string) =>
      api.post('/api/messages', partnerId ? { recipient_id: partnerId, content } : { content }),
    onSuccess: () => {
      setDraft('');
      qc.invalidateQueries({ queryKey: ['conversations'] });
      qc.invalidateQueries({ queryKey: ['thread', partnerId] });
    },
  });

  const firstName = partner.name.includes('@') ? partner.name : partner.name.split(' ')[0];

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Who you're talking to */}
      <div
        className="mx-4 mb-3 flex items-center gap-3 rounded-lg px-4 py-3"
        style={{ background: 'var(--surface)', border: '1px solid rgba(244,239,227,0.08)' }}
      >
        <Avatar name={partner.name} url={partner.avatar} />
        <div className="min-w-0">
          <p className="font-semibold truncate" style={{ color: 'var(--cream)' }}>{partner.name}</p>
          <p className="text-xs" style={{ color: 'var(--gray-400)' }}>{partner.label}</p>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 flex flex-col" style={{ paddingBottom: 12 }}>
        {thread.isLoading ? (
          <p className="text-sm" style={{ color: 'var(--cream-muted)' }}>Loading…</p>
        ) : messages.length === 0 ? (
          <p className="text-sm text-center mt-8" style={{ color: 'var(--cream-muted)' }}>
            {clientWithoutThread ? 'Send a message to reach your coach.' : 'No messages yet. Say hello.'}
          </p>
        ) : (
          messages.map((m, i) => {
            const mine = m.sender_id === meId;
            const startsRun = i === 0 || messages[i - 1].sender_id !== m.sender_id;
            return (
              <div key={m.id} className={`flex flex-col ${mine ? 'items-end' : 'items-start'} ${startsRun ? 'mt-4' : 'mt-1'}`}>
                {startsRun && (
                  <div className={`flex items-center gap-2 mb-1.5 ${mine ? 'flex-row-reverse' : ''}`}>
                    {mine
                      ? me && <Avatar name={me.name} url={me.avatar} size={24} />
                      : <Avatar name={partner.name} url={partner.avatar} size={24} />}
                    <span className="text-xs font-medium" style={{ color: mine ? 'var(--gray-400)' : 'var(--cream-dim)' }}>
                      {mine ? 'You' : partner.name}
                    </span>
                  </div>
                )}
                <div
                  className="text-sm leading-relaxed whitespace-pre-wrap"
                  style={{
                    maxWidth: '80%',
                    padding: '0.6rem 0.85rem',
                    borderRadius: 14,
                    background: mine ? 'var(--champagne)' : 'var(--surface-elevated)',
                    color: mine ? 'var(--ink)' : 'var(--cream-dim)',
                    borderBottomRightRadius: mine ? 4 : 14,
                    borderBottomLeftRadius: mine ? 14 : 4,
                    marginLeft: mine ? 0 : 32,
                  }}
                >
                  {m.content}
                </div>
                <span className="text-[10px] mt-1 px-1" style={{ color: 'var(--cream-muted)', marginLeft: mine ? 0 : 32 }}>
                  {fmtTime(m.created_at)}
                </span>
              </div>
            );
          })
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (draft.trim()) send.mutate(draft.trim());
        }}
        className="flex gap-2 px-4 pt-3"
        style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))', borderTop: '1px solid var(--ink-line, rgba(244,239,227,0.08))' }}
      >
        <input
          className="input flex-1"
          placeholder={clientWithoutThread ? 'Message your coach…' : `Message ${firstName}…`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          aria-label="Message"
        />
        <button type="submit" className="btn-gold px-5" disabled={send.isPending || !draft.trim()}>
          Send
        </button>
      </form>
    </div>
  );
}
